/**
 * Partner connect entry point (EPD-11850) — `/connect?source=<partner>&return=<url>`.
 *
 * Partners link creators here. Signed-out creators are sent through login (or
 * signup) with this URL as `returnTo`, so the source survives the round trip.
 * Signed-in creators skip onboarding and land on their creator org's connect
 * page. An unknown or missing source gets the standard flow.
 */

import { ensureOrgBootstrap, pickCreatorOrgSlug } from "@/lib/org-bootstrap";
import { getPartnerSource, MAX_RETURN_URL_LENGTH } from "@/lib/partner-source";
import { organizationListQueryOptions } from "@/lib/queries/organization";
import { getCachedSession, sessionQueryOptions } from "@/lib/queries/session";
import { trpcClient } from "@/lib/trpc";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

export const connectEntrySearchSchema = z.object({
  source: z.string().optional().catch(undefined),
  // Where the partner wants the creator sent back; validated on the connect page
  return: z.string().max(MAX_RETURN_URL_LENGTH).optional().catch(undefined),
});

export const Route = createFileRoute("/connect")({
  validateSearch: connectEntrySearchSchema,
  beforeLoad: async ({ context, location, search }) => {
    const partner = getPartnerSource(search.source);
    if (!partner) throw redirect({ to: "/" });

    let session = getCachedSession(context.queryClient);
    if (session === undefined) {
      session = await context.queryClient.fetchQuery(sessionQueryOptions());
    }
    if (!session?.user || !session?.session) {
      throw redirect({ to: "/login", search: { returnTo: location.href } });
    }

    // Create or finish the creator org so onboarding never intercepts the flow
    await ensureOrgBootstrap(context.queryClient);
    let organizations = await context.queryClient.fetchQuery({
      ...organizationListQueryOptions(),
      staleTime: 0,
    });
    let orgSlug = pickCreatorOrgSlug(organizations);

    // createAuto leaves a bare (untyped) org; finish it as a creator org
    if (!orgSlug) {
      await trpcClient.organization.skipOnboarding.mutate();
      organizations = await context.queryClient.fetchQuery({
        ...organizationListQueryOptions(),
        staleTime: 0,
      });
      orgSlug = pickCreatorOrgSlug(organizations);
    }
    if (!orgSlug) throw redirect({ to: "/" });

    throw redirect({
      to: "/o/$orgSlug/connect",
      params: { orgSlug },
      search: { source: partner.id, return: search.return },
    });
  },
});
